import React, { useState, useRef, useEffect } from 'react';
import { useHighlightModelStore, useMergeHighlightModelStore, usePDFAnnotationStore, useSolutionLinkModelStore } from '../../../store/pdf-pin.store';
import { checkStrokeOverlap, distanceToSegment, FINAL_COLOR, getBestSegmentForIcon, PREVIEW_COLOR, shortUUID, simplifyPathFromPolygraph, svgUrlToPngBytes } from '../utils/pdf-pin.shared';
import { Dropdown, DropdownProps } from 'primereact/dropdown';
import { PdfHighlighterEntity, SystemDDLEntityForHighlights } from '../../../interface/pdf-pin';
import { IsNullOrEmptyArray, IsNullOrUndefined, IsStringNullEmptyOrUndefined } from '../../../utils/null-check';
import { useCanvasHoverCursor } from '../../../hooks/useCanvaHoverCursor';
import { toast } from 'react-toastify';
import { toastOptObj } from '../../../utils/constants';
import { fetchStoredWaltzSolutions } from '../../../service/shared.service';
import { SolutionDetails } from '../../../interface/order';
import { useParams } from 'react-router-dom';
import { v4 as uuid4 } from "uuid"

interface HighlightMarkerProps {
    pageRef: React.RefObject<HTMLDivElement>;
    isAnnotation: boolean;
}

function drawNumberIconOnCanvas(
    ctx: CanvasRenderingContext2D,
    {
        x,
        y,
        number,
        size = 24,
        color = "#000000",
        opacity = 0.8,
        borderWidth = 1,
    }: any
) {
    const radius = size / 2;


    ctx.save();
    ctx.globalAlpha = opacity;

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 0.8;
    ctx.strokeStyle = color;
    ctx.globalAlpha = opacity;
    ctx.stroke();
    ctx.restore();

    // Number
    ctx.save();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = `${Math.floor(size * 0.5)}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(number), x, y);
    ctx.globalAlpha = opacity;
    ctx.restore();
}



const HighlightMarker: React.FC<HighlightMarkerProps> = ({ pageRef, isAnnotation }) => {

    const { orderId }: any = useParams();

    const canvasRef = useRef<HTMLCanvasElement>(null);

    const { isHighlightsActive, highlights, addHighlight, selectedPageId, systemDDL, updateHighlight, addHighlightToPage, updateHighlightInPage, setSelectedHighlight, width, iconCache, setIconCache, isLinkActive, isMoveActive } = usePDFAnnotationStore();

    const [draftDropdownPos, setDraftDropdownPos] = useState<{ x: number; y: number } | null>(null);

    const { onOpen } = useHighlightModelStore();

    const { onOpen: onMergedOpen, setOverlapHighligh } = useMergeHighlightModelStore();

    const [dropdownAbove, setDropdownAbove] = useState(false);

    const { onOpen: onSolutionLinkOpen } = useSolutionLinkModelStore()

    const [isDrawStart, setIsDrawStart] = useState(false);

    const [scale] = useState(1);

    const [solutionList] = useState<SolutionDetails[] | []>(() => {
        const waltzOrderDetail = fetchStoredWaltzSolutions(orderId);
        if (waltzOrderDetail && !IsNullOrEmptyArray(waltzOrderDetail)) {
            return waltzOrderDetail;
        }
        return []
    })

    const redraw = () => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        if (!canvas || !ctx) return;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.lineCap = 'butt';
        ctx.lineJoin = 'miter';

        const rect = pageRef.current!.getBoundingClientRect();
        const pageStrokes = highlights

        for (const s of pageStrokes) {

            ctx.strokeStyle = s.color;
            ctx.lineWidth = s.width * scale;
            ctx.globalAlpha = s.isDraft ? 1 : 0.5;
            ctx.beginPath();
            s.points.forEach((pt, idx) => {
                if (pt.x == null || pt.y == null) {
                    ctx.stroke();
                    ctx.beginPath();
                    return;
                }
                const x = (pt.x / 100) * rect.width;
                const y = (pt.y / 100) * rect.height;
                if (idx === 0 || s.points[idx - 1].x == null) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            });
            ctx.stroke();

            if (s.isDraft) {
                const rectW = rect.width;
                const rectH = rect.height;

                const pts = s.points.filter(p => p.x != null && p.y != null);
                if (pts.length < 2) continue;

                // --- Build tight bounding box in *pixels* ---
                let minXp = Infinity, minYp = Infinity, maxXp = -Infinity, maxYp = -Infinity;
                for (const p of pts) {
                    const xpx = (p.x / 100) * rectW;
                    const ypx = (p.y / 100) * rectH;
                    if (xpx < minXp) minXp = xpx;
                    if (xpx > maxXp) maxXp = xpx;
                    if (ypx < minYp) minYp = ypx;
                    if (ypx > maxYp) maxYp = ypx;
                }

                // Stroke thickness + breathing room
                const linePx = (s.width ?? width) * scale;
                const gap = Math.max(12, Math.round(linePx * 1.2)); // safe visual gap

                const centerX = (minXp + maxXp) / 2;
                const bottomEdge = maxYp + linePx / 2;
                const topEdge = minYp - linePx / 2;

                // Estimated dropdown size
                const estH = 44;
                const estHalfW = 110;

                // Check if bottom edge is too close to bottom of PDF page
                const spaceBelow = rectH - bottomEdge;
                const spaceAbove = topEdge;

                // Place below if enough space, otherwise force above
                let placeBelow = spaceBelow >= (estH + gap);

                // If bottom is at dead end (very little space), override
                if (spaceBelow < gap) {
                    placeBelow = false; // force above
                }

                let x = centerX;
                let y = placeBelow ? (bottomEdge + gap) : (topEdge - gap);

                // Clamp horizontally so it doesn't go off the PDF/page
                x = Math.max(estHalfW + 4, Math.min(rectW - estHalfW - 4, x));

                setDraftDropdownPos({ x, y });
                setDropdownAbove(!placeBelow);
            }

            ctx.globalAlpha = 1;


            if (s.systemIcon && s.points.length >= 2) {
                const drawIcon = (img: HTMLImageElement | null, draw = 1, number = 0) => {
                    const bestSeg = getBestSegmentForIcon(s.points);
                    if (!bestSeg) return;

                    if (!pageRef.current) return;

                    const rect = pageRef.current.getBoundingClientRect();
                    const startX = (bestSeg.start.x / 100) * rect.width;
                    const startY = (bestSeg.start.y / 100) * rect.height;
                    const endX = (bestSeg.end.x / 100) * rect.width;
                    const endY = (bestSeg.end.y / 100) * rect.height;

                    const midX = (startX + endX) / 2;
                    const midY = (startY + endY) / 2;
                    const dx = endX - startX;
                    const dy = endY - startY;
                    const lenVec = Math.hypot(dx, dy);
                    if (lenVec === 0) return;

                    const unitPerpX = -dy / lenVec;
                    const unitPerpY = dx / lenVec;
                    const iconSize = 24;
                    const offset = -24;

                    const iconX = midX + unitPerpX * offset;
                    const iconY = midY + unitPerpY * offset;

                    if (img && !IsNullOrUndefined(img)) {
                        ctx.save();
                        ctx.globalAlpha = draw;
                        ctx.drawImage(img, iconX - iconSize / 2, iconY - iconSize / 2, iconSize, iconSize);
                    } else {
                        drawNumberIconOnCanvas(ctx, {
                            x: iconX,
                            y: iconY,
                            number: number,      // or any number you want to show
                            size: iconSize,
                            color: s.color,    // use stroke color
                            opacity: draw,
                            borderWidth: 2,
                        });
                    }


                };

                if (s.solutionId && !IsStringNullEmptyOrUndefined(s.solutionId) && solutionList && !IsNullOrEmptyArray(solutionList)) {
                    const solutionDetails = solutionList.find((item) => item._id === s.solutionId);
                    if (solutionDetails && !IsNullOrUndefined(solutionList)) {
                        drawIcon(null, 0.8, solutionDetails.solutionNo);

                    }
                } else {
                    const cachedImg = iconCache.get(s.id);
                    if (cachedImg) {
                        drawIcon(cachedImg);
                    } else {
                        svgUrlToPngBytes(s.systemIcon, s.color, 60, 60)
                            .then((iconPngBytes: any) => {
                                const blob = new Blob([iconPngBytes], { type: "image/png" });
                                const url = URL.createObjectURL(blob);
                                const img = new Image();
                                img.onload = () => {
                                    setIconCache(s.id, img);
                                    drawIcon(img, 0.8);
                                };
                                img.src = url;
                            })
                            .catch(err => console.error("Error loading system icon:", err));
                    }
                }
            }


        };


    };

    const selectedSystemTemplate = (option: SystemDDLEntityForHighlights, props: DropdownProps) => {
        if (!IsNullOrUndefined(option)) {
            return (
                <div className="d-flex align-items-center gap-2">
                    <img src={option.systemIcon} style={{ width: '25px' }} />
                    <div>{option.systemName}</div>
                </div>
            );
        }
        return <span>{props.placeholder}</span>;
    };

    const systemOptionTemplate = (option: SystemDDLEntityForHighlights) => {
        return (
            <div className="d-flex align-items-center gap-2">
                <img src={option.systemIcon} style={{ width: '25px' }} />
                <div>{option.systemName}</div>
            </div>
        );
    };

    const handleSelectedSystem = (value: string) => {
        const draftHighlight: PdfHighlighterEntity | undefined = highlights.find((item) => item.isDraft);
        if (draftHighlight) {
            const selectedSystem = systemDDL.find((item) => item.id === value);
            const points = simplifyPathFromPolygraph(draftHighlight.points, 2);
            updateHighlight(draftHighlight.id, { ...draftHighlight, points: points, color: FINAL_COLOR, isDraft: false, systemIcon: selectedSystem?.systemIcon, systemId: selectedSystem?.id, systemName: selectedSystem?.systemName })
            updateHighlightInPage(selectedPageId, draftHighlight.id, { ...draftHighlight, points: points, color: FINAL_COLOR, isDraft: false, systemIcon: selectedSystem?.systemIcon, systemId: selectedSystem?.id, systemName: selectedSystem?.systemName })
            setDraftDropdownPos(null);
            setIsDrawStart(true);
            redraw();
        }
    }

    const handleCheckDraftHighlight = (): boolean => highlights.some((item) => item.isDraft);


    const handleCanvasClick = (e: MouseEvent) => {
        if (isMoveActive) return;
        if (!isAnnotation) return;
        if (isHighlightsActive) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const clickedHighlight = highlights.find((highlight) => {
            if (highlight.isDraft) return false; // ignore draft highlights
            const pts = highlight.points.filter(p => p.x != null && p.y != null);
            if (pts.length < 2) return false;

            for (let i = 0; i < pts.length - 1; i++) {
                const x1 = (pts[i].x / 100) * rect.width;
                const y1 = (pts[i].y / 100) * rect.height;
                const x2 = (pts[i + 1].x / 100) * rect.width;
                const y2 = (pts[i + 1].y / 100) * rect.height;

                const dist = distanceToSegment({ x: clickX, y: clickY }, { x: x1, y: y1 }, { x: x2, y: y2 });
                if (dist <= 5) return true;
            }
            return false;
        });

        if (clickedHighlight) {
            setSelectedHighlight(clickedHighlight.id);
            if (isLinkActive) {
                onSolutionLinkOpen();
            } else {
                onOpen()

            }
        }
    };


    useEffect(() => {
        const pageEl = pageRef.current;
        const canvas = canvasRef.current;
        if (!pageEl || !canvas) return;


        const resize = () => {
            const rect = pageEl.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;
            redraw();
        };


        const observer = new ResizeObserver(resize);
        observer.observe(pageEl);
        resize();
        return () => observer.disconnect();
    }, [pageRef, highlights]);

    useEffect(() => {
        if (!isHighlightsActive) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        let drawing = false;
        let currentPoints: { x: number; y: number }[] = [];
        let continuingDraft = false;
        let draftStrokeId: string | null = null;

        const rectProvider = () => pageRef.current!.getBoundingClientRect();

        const pointerDown = (e: PointerEvent) => {
            const rect = rectProvider();
            const x = e.offsetX;
            const y = e.offsetY;
            const normX = (x / rect.width) * 100;
            const normY = (y / rect.height) * 100;



            const pageDraftStroke = highlights.find(
                st => st.isDraft
            );

            if (pageDraftStroke) {
                const lastPoint = pageDraftStroke.points[pageDraftStroke.points.length - 1];
                const continueThreshold = 2;
                if (Math.hypot(lastPoint.x - normX, lastPoint.y - normY) <= continueThreshold) {
                    setIsDrawStart(true);
                    drawing = true;
                    continuingDraft = true;
                    draftStrokeId = pageDraftStroke.id;
                    currentPoints = pageDraftStroke.points;
                    ctx.strokeStyle = PREVIEW_COLOR;
                    ctx.lineWidth = width * scale;
                    ctx.globalAlpha = 0.5;
                    ctx.lineCap = 'butt';
                    ctx.lineJoin = 'miter';
                    ctx.beginPath();
                    ctx.moveTo(
                        (lastPoint.x / 100) * rect.width,
                        (lastPoint.y / 100) * rect.height
                    );
                } else {
                    return;
                }
            } else {
                setIsDrawStart(true);
                drawing = true;
                continuingDraft = false;
                draftStrokeId = null;
                currentPoints = [];
                ctx.strokeStyle = PREVIEW_COLOR;
                ctx.lineWidth = width * scale;
                ctx.globalAlpha = 0.5;
                ctx.lineCap = 'butt';
                ctx.lineJoin = 'miter';
                ctx.beginPath();
                ctx.moveTo(x, y);
                currentPoints.push({ x: normX, y: normY });
            }
        };

        const pointerMove = (e: PointerEvent) => {
            if (!drawing) {
                setIsDrawStart(false);
                return
            };
            setIsDrawStart(true);
            const x = e.offsetX;
            const y = e.offsetY;
            ctx.lineTo(x, y);
            ctx.stroke();
            const rect = rectProvider();
            currentPoints.push({
                x: parseFloat(((x / rect.width) * 100).toFixed(4)),
                y: parseFloat(((y / rect.height) * 100).toFixed(4))
            });
        };

        const pointerUp = () => {
            if (!drawing) {
                setIsDrawStart(false);
                return
            };
            setIsDrawStart(false);
            drawing = false;
            ctx.globalAlpha = 1;
            const overlaps = checkStrokeOverlap(
                { points: currentPoints, width },
                highlights,
                0.5,

            );
            if (overlaps.length > 0) {

                const firstStrokeSystemId = overlaps[0].targetStroke.systemId;
                if (!overlaps.every((item) => item.targetStroke.systemId === firstStrokeSystemId)) {
                    toast.error("Highlights can be merged only if they belong to the same system.", { ...toastOptObj });
                    redraw();
                    return;
                }
                setOverlapHighligh((overlaps as any[])
                    .map(o => o.targetStroke), currentPoints);
                onMergedOpen();

                // ctx.clearRect(0, 0, canvas.width, canvas.height);
            }



            if (currentPoints.length > 1) {
                if (continuingDraft && draftStrokeId) {
                    redraw();
                } else {
                    const id = uuid4();
                    addHighlight({
                        id,
                        pageId: selectedPageId,
                        systemIcon: "",
                        systemId: "",
                        systemName: "",
                        points: currentPoints,
                        color: PREVIEW_COLOR,
                        width,
                        isDraft: true,
                        solutionId: ""
                    });
                    addHighlightToPage(selectedPageId, {
                        id,
                        pageId: selectedPageId,
                        systemIcon: "",
                        systemId: "",
                        systemName: "",
                        points: currentPoints,
                        color: PREVIEW_COLOR,
                        width,
                        isDraft: true,
                        solutionId: ""
                    });
                }
            }
        };

        canvas.addEventListener('pointerdown', pointerDown);
        canvas.addEventListener('pointermove', pointerMove);
        window.addEventListener('pointerup', pointerUp);

        return () => {
            canvas.removeEventListener('pointerdown', pointerDown);
            canvas.removeEventListener('pointermove', pointerMove);
            window.removeEventListener('pointerup', pointerUp);
        };
    }, [isHighlightsActive, highlights, pageRef.current]);

    useEffect(() => {
        const t = setTimeout(() => redraw(), 10);
        return () => clearTimeout(t);
    }, [highlights]);


    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.addEventListener('click', handleCanvasClick);
        return () => {
            canvas.removeEventListener('click', handleCanvasClick);
        };
    }, [isHighlightsActive, isLinkActive, highlights]);

    useCanvasHoverCursor({ canvasRef, highlights, isHighlightsActive, isMoveActive });

    return <>
        <canvas
            ref={canvasRef}
            style={{ position: 'absolute', top: 0, left: 0, pointerEvents: "auto", zIndex: 5 }}
        />
        {(handleCheckDraftHighlight() && draftDropdownPos && !isDrawStart) && (
            <Dropdown
                value={""}
                options={systemDDL}
                onChange={(e) => { handleSelectedSystem(e.target.value) }}
                valueTemplate={selectedSystemTemplate}
                optionLabel='systemName'
                itemTemplate={systemOptionTemplate}
                optionValue='id'
                placeholder='Select system'
                style={{
                    position: 'absolute',
                    top: draftDropdownPos.y,
                    left: draftDropdownPos.x,
                    width: "200px",
                    // below the box -> anchor by the dropdown's top;
                    // above the box -> anchor by the dropdown's bottom
                    transform: dropdownAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
                    zIndex: 10
                }}
                panelStyle={{
                    width: "200px"
                }}
            />
        )}
    </>
}

export default HighlightMarker;
